import React, { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    FaSearch,
    FaFilter,
    FaTimes,
    FaHeart,
    FaRegHeart,
    FaStar,
    FaMapMarkerAlt,
    FaChevronDown,
    FaChevronUp,
    FaChevronLeft,
    FaChevronRight,
    FaBuilding,
} from 'react-icons/fa';
import { FiArrowUpRight } from 'react-icons/fi';
import { Sparkles } from 'lucide-react';
import { toast } from 'react-hot-toast';
import * as offMarketStaysService from '../services/offMarketStaysService';
import { getOffMarketSpecialCommissionBreakdown } from '../utils/offMarketSpecialCommission';
import MainLayout from '../components/Layout/MainLayout';

function formatCommissionLabel(commissionBd, specialCommission) {
    if (!commissionBd && specialCommission?.percentage != null) {
        return `Commission ${specialCommission.percentage}%`;
    }
    if (!commissionBd) return null;
    if (commissionBd.specialAdded > 0) {
        return `Commission ${commissionBd.total}% (+${commissionBd.specialAdded}%)`;
    }
    return `Commission ${commissionBd.total}%`;
}

// ─── helpers ──────────────────────────────────────────────────────────────────

const HERO_FALLBACK =
    'https://images.pexels.com/photos/1134176/pexels-photo-1134176.jpeg?auto=compress&cs=tinysrgb&w=1600&h=900&dpr=1';

function getCoverImage(property) {
    const imgs = property?.adminImages;
    if (!Array.isArray(imgs) || imgs.length === 0) return HERO_FALLBACK;
    const prioritised = [...imgs].sort((a, b) => {
        if (a.categoryCode === 1) return -1;
        if (b.categoryCode === 1) return 1;
        return 0;
    });
    return prioritised[0]?.s3Url || prioritised[0]?.sabreImageUrl || HERO_FALLBACK;
}

function getHeroSubtitle(property) {
    if (!property) return '';
    const city = property.location?.city;
    const country = property.location?.countryName;
    if (city && country) return `${city} · ${country}`;
    if (country) return country;
    if (property.chainName) return property.chainName;
    return 'Exclusive off-market stay';
}

function formatRating(r) {
    if (r == null || Number.isNaN(Number(r))) return null;
    const n = Number(r);
    if (n <= 0) return null;
    return n.toFixed(1);
}

/** Default nights on the booking bar when opening from off-market stays (matches typical hotel-search flow). */
const DEFAULT_STAY_NIGHTS = 6;

function formatIsoLocalDate(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

/**
 * Builds `/hotel/:hotelCode?checkInDate=…&checkOutDate=…&…&offMarketStay=1&offMarketPropertyId=…`
 * so HotelDetailsPage can read query params and you can identify the off-market Mongo document.
 */
function buildHotelDetailsUrlFromOffMarketProperty(property) {
    const code = property?.hotelCode;
    if (code == null || String(code).trim() === '') return null;
    const checkIn = new Date();
    checkIn.setDate(checkIn.getDate() + 1);
    const checkOut = new Date(checkIn);
    checkOut.setDate(checkOut.getDate() + DEFAULT_STAY_NIGHTS);
    const params = new URLSearchParams({
        checkInDate: formatIsoLocalDate(checkIn),
        checkOutDate: formatIsoLocalDate(checkOut),
        adults: '1',
        children: '0',
        currency: 'USD',
        offMarketStay: '1',
    });
    if (property._id != null) {
        params.set('offMarketPropertyId', String(property._id));
    }
    const hotelCode = encodeURIComponent(String(code).trim());
    return `/hotel/${hotelCode}?${params.toString()}`;
}

// ─── component ────────────────────────────────────────────────────────────────

const OffMarketStaysPage = () => {
    const navigate = useNavigate();

    const [properties, setProperties] = useState([]);
    const [filteredProperties, setFilteredProperties] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [favorites, setFavorites] = useState([]);

    // Filter states
    const [selectedCountries, setSelectedCountries] = useState([]);
    const [selectedRating, setSelectedRating] = useState('');
    const [sortBy, setSortBy] = useState('latest');

    // Country list for filter
    const [countries, setCountries] = useState([]);

    // Collapsible section states
    const [expandedSections, setExpandedSections] = useState({ countries: true, rating: true });

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const observerTarget = useRef(null);

    // Hero carousel
    const [heroSlideIndex, setHeroSlideIndex] = useState(0);
    const [heroMediaOpacity, setHeroMediaOpacity] = useState(1);
    const heroFirstPaint = useRef(true);

    const goToHotelProperty = useCallback(
        (property) => {
            const url = buildHotelDetailsUrlFromOffMarketProperty(property);
            if (!url) {
                toast.error('This property does not have a hotel code.');
                return;
            }
            navigate(url);
        },
        [navigate]
    );

    // ── favorites ──────────────────────────────────────────────────────────────
    useEffect(() => {
        try {
            const stored = localStorage.getItem('travelAgent_favoriteOffMarketStays');
            if (stored) setFavorites(JSON.parse(stored));
        } catch { /* ignore */ }
    }, []);
    useEffect(() => {
        localStorage.setItem('travelAgent_favoriteOffMarketStays', JSON.stringify(favorites));
    }, [favorites]);

    const toggleFavorite = (id) =>
        setFavorites((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    const isFavorite = (id) => favorites.includes(id);

    // ── load countries ─────────────────────────────────────────────────────────
    useEffect(() => {
        let cancelled = false;
        offMarketStaysService.getOffMarketStayCountries()
            .then((res) => { if (!cancelled && res?.success) setCountries(res.data || []); })
            .catch(() => { });
        return () => { cancelled = true; };
    }, []);

    // ── load properties ────────────────────────────────────────────────────────
    const loadProperties = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const res = await offMarketStaysService.getAllOffMarketStays({ page: currentPage, limit: 12 });
            if (res?.success && res.data) {
                setProperties((prev) => currentPage === 1 ? res.data : [...prev, ...res.data]);
                setHasMore(res.pagination?.hasNextPage || false);
            } else {
                setError('Failed to load off-market stays');
            }
        } catch (err) {
            setError(err.message || 'Failed to load off-market stays');
        } finally {
            setLoading(false);
        }
    }, [currentPage]);

    useEffect(() => { loadProperties(); }, [loadProperties]);

    // ── filter + sort ──────────────────────────────────────────────────────────
    useEffect(() => {
        let filtered = [...properties];
        if (searchTerm.trim()) {
            const q = searchTerm.toLowerCase();
            filtered = filtered.filter((p) =>
                p.name?.toLowerCase().includes(q) ||
                p.location?.city?.toLowerCase().includes(q) ||
                p.location?.countryName?.toLowerCase().includes(q) ||
                p.chainName?.toLowerCase().includes(q) ||
                p.hotelCode?.toLowerCase().includes(q)
            );
        }
        if (selectedCountries.length > 0) {
            filtered = filtered.filter((p) => selectedCountries.includes(p.location?.countryCode));
        }
        if (selectedRating) {
            const min = parseFloat(selectedRating);
            filtered = filtered.filter((p) => p.sabreRating != null && Number(p.sabreRating) >= min);
        }
        filtered.sort((a, b) => {
            switch (sortBy) {
                case 'rating-high': return (b.sabreRating || 0) - (a.sabreRating || 0);
                case 'name-az': return (a.name || '').localeCompare(b.name || '');
                case 'name-za': return (b.name || '').localeCompare(a.name || '');
                default: return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
            }
        });
        setFilteredProperties(filtered);
    }, [properties, searchTerm, selectedCountries, selectedRating, sortBy]);

    // ── infinite scroll ────────────────────────────────────────────────────────
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => { if (entries[0].isIntersecting && hasMore && !loading) setCurrentPage((p) => p + 1); },
            { threshold: 1 }
        );
        if (observerTarget.current) observer.observe(observerTarget.current);
        return () => observer.disconnect();
    }, [hasMore, loading]);

    // ── hero carousel ──────────────────────────────────────────────────────────
    const heroSlides = useMemo(() => (properties || []).filter(Boolean).slice(0, 10), [properties]);
    const heroCount = heroSlides.length;
    const activeHeroIndex = heroCount ? heroSlideIndex % heroCount : 0;
    const currentHero = heroCount ? heroSlides[activeHeroIndex] : null;
    const nextHero = heroCount > 1 ? heroSlides[(activeHeroIndex + 1) % heroCount] : null;
    const heroPoster = currentHero ? getCoverImage(currentHero) : HERO_FALLBACK;

    useEffect(() => {
        if (heroCount < 2) return;
        const t = setInterval(() => setHeroSlideIndex((i) => (i + 1) % heroCount), 18000);
        return () => clearInterval(t);
    }, [heroCount]);

    const heroVisualKey = currentHero?._id ?? 'empty';
    useLayoutEffect(() => {
        if (heroFirstPaint.current) { heroFirstPaint.current = false; return; }
        setHeroMediaOpacity(0);
        const t = window.setTimeout(() => setHeroMediaOpacity(1), 100);
        return () => window.clearTimeout(t);
    }, [heroVisualKey]);

    const goHeroPrev = () => { if (heroCount) setHeroSlideIndex((i) => (i - 1 + heroCount) % heroCount); };
    const goHeroNext = () => { if (heroCount) setHeroSlideIndex((i) => (i + 1) % heroCount); };

    // ── filter helpers ─────────────────────────────────────────────────────────
    const clearFilters = () => { setSelectedCountries([]); setSelectedRating(''); setSearchTerm(''); };
    const toggleSection = (s) => setExpandedSections((p) => ({ ...p, [s]: !p[s] }));
    const hasActiveFilters = selectedCountries.length > 0 || selectedRating;

    const ratingOptions = [
        { value: '4', label: '4+ stars' },
        { value: '3', label: '3+ stars' },
        { value: '2', label: '2+ stars' },
    ];

    // ─────────────────────────────────────────────────────────────────────────
    return (
        <MainLayout>
            <div className="min-h-screen" style={{ backgroundColor: '#f7f7f7' }}>

                {/* ── Hero ── */}
                <section className="relative overflow-hidden bg-white" style={{ minHeight: 'min(62svh, 36rem)' }}>

                    {/* Right panel: full-height property image */}
                    <div className="hidden lg:block absolute top-0 right-0 bottom-0 w-[55%] z-0 overflow-hidden"
                        style={{ clipPath: 'polygon(10% 0, 100% 0, 100% 100%, 0% 100%)' }}>
                        <div className="absolute inset-0 transition-opacity duration-700 ease-out" style={{ opacity: heroMediaOpacity }}>
                            <img
                                src={heroPoster}
                                alt=""
                                className="w-full h-full object-cover transition-transform duration-700"
                            />
                        </div>

                        {/* Floating property card */}
                        {currentHero && (
                            <div className="absolute bottom-8 right-7 z-10">
                                <div className="bg-white/90 backdrop-blur-sm border border-white/60 px-5 py-4 max-w-[240px] overflow-hidden" style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
                                    <p className="text-[9px] tracking-[0.3em] uppercase font-poppinsLight text-gray-400 mb-1.5 truncate">Featured Property</p>
                                    <p className="font-AXONALLPrimeRegular uppercase text-sm tracking-wide leading-tight mb-1 text-gray-900 line-clamp-2">{currentHero.name}</p>
                                    <p className="text-[9px] tracking-[0.2em] uppercase font-poppinsLight text-gray-400 truncate">{getHeroSubtitle(currentHero)}</p>
                                </div>
                            </div>
                        )}

                        {/* Slide nav */}
                        {heroCount > 1 && (
                            <div className="absolute bottom-8 left-6 z-10 flex items-center gap-1">
                                <button type="button" onClick={goHeroPrev}
                                    className="p-2 bg-white/80 backdrop-blur-sm text-gray-700 hover:bg-white hover:text-gray-900 transition-all duration-200"
                                    aria-label="Previous">
                                    <FaChevronLeft size={10} />
                                </button>
                                <button type="button" onClick={goHeroNext}
                                    className="p-2 bg-white/80 backdrop-blur-sm text-gray-700 hover:bg-white hover:text-gray-900 transition-all duration-200"
                                    aria-label="Next">
                                    <FaChevronRight size={10} />
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Mobile background */}
                    <div className="lg:hidden absolute inset-0 z-0">
                        <div className="absolute inset-0 transition-opacity duration-700" style={{ opacity: heroMediaOpacity }}>
                            <img src={heroPoster} alt="" className="w-full h-full object-cover" />
                        </div>
                        <div className="absolute inset-0 bg-black/65" />
                    </div>

                    {/* Left content panel */}
                    <div className="relative z-10 lg:w-[50%] flex flex-col">

                        {/* Top strip */}
                        <div className="border-b border-white/20 lg:border-gray-100 px-6 sm:px-8 lg:px-12 py-3 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <FaBuilding className="w-3.5 h-3.5 text-white/50 lg:text-gray-400" aria-hidden />
                                <span className="text-[9px] tracking-[0.35em] uppercase font-poppinsLight text-white/50 lg:text-gray-400">
                                    Axonall Off-Market Stays
                                </span>
                            </div>
                            {heroCount > 1 && (
                                <span className="text-[9px] tracking-[0.25em] font-poppinsLight text-white/35 lg:text-gray-300">
                                    {String(activeHeroIndex + 1).padStart(2, '0')} / {String(heroCount).padStart(2, '0')}
                                </span>
                            )}
                        </div>

                        {/* Main text */}
                        <div className="flex-1 flex flex-col justify-center px-6 sm:px-8 lg:px-12 py-6">

                            <div className="flex items-center gap-3 mb-4 overflow-hidden">
                                <div className="w-6 h-px bg-white/30 lg:bg-gray-300 shrink-0" />
                                <span className="text-[9px] tracking-[0.2em] uppercase font-poppinsLight text-white/50 lg:text-gray-400 min-w-0 truncate">
                                    {currentHero ? getHeroSubtitle(currentHero) : 'Exclusive Properties · Curated Collection'}
                                </span>
                            </div>

                            <h1 className="font-AXONALLPrimeRegular uppercase tracking-wide text-white lg:text-gray-900 mb-4 overflow-hidden"
                                style={{ fontSize: 'clamp(1.6rem, 3vw, 2.6rem)', lineHeight: '1.2' }}>
                                {currentHero ? (
                                    <span className="block line-clamp-3">{currentHero.name}</span>
                                ) : (
                                    <>
                                        <span className="block">Stay Exclusive.</span>
                                        <span className="block">Live Privately.</span>
                                    </>
                                )}
                            </h1>

                            <div className="flex gap-4 mb-5">
                                <div className="w-px bg-white/25 lg:bg-gray-200 shrink-0 mt-1" />
                                <p className="text-white/65 lg:text-gray-500 font-poppinsLight text-sm leading-relaxed max-w-xs">
                                    Hand-picked off-market properties unavailable to the public — accessible exclusively through Axonall Reserve advisors.
                                </p>
                            </div>

                            <div className="flex flex-wrap gap-3 mb-5">
                                {currentHero ? (
                                    <button
                                        type="button"
                                        onClick={() => goToHotelProperty(currentHero)}
                                        className="inline-flex items-center gap-2.5 px-6 py-3 lg:px-7 lg:py-3.5 bg-white text-gray-900 lg:bg-gray-900 lg:text-white text-[10px] tracking-[0.25em] uppercase font-poppinsRegular hover:opacity-90 transition-all duration-200"
                                    >
                                        View Property
                                        <FiArrowUpRight className="text-sm" strokeWidth={2.5} />
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={() => document.getElementById('stays-grid')?.scrollIntoView({ behavior: 'smooth' })}
                                        className="inline-flex items-center gap-2.5 px-6 py-3 lg:px-7 lg:py-3.5 bg-white text-gray-900 lg:bg-gray-900 lg:text-white text-[10px] tracking-[0.25em] uppercase font-poppinsRegular hover:opacity-90 transition-all duration-200"
                                    >
                                        Browse Collection
                                        <FiArrowUpRight className="text-sm" strokeWidth={2.5} />
                                    </button>
                                )}
                                <button
                                    type="button"
                                    onClick={() => document.getElementById('stays-grid')?.scrollIntoView({ behavior: 'smooth' })}
                                    className="inline-flex items-center gap-2.5 px-6 py-3 lg:px-7 lg:py-3.5 border border-white/50 text-white lg:border-gray-300 lg:text-gray-700 text-[10px] tracking-[0.25em] uppercase font-poppinsRegular hover:border-white lg:hover:border-gray-900 lg:hover:text-gray-900 transition-all duration-200"
                                >
                                    Browse All
                                </button>
                            </div>

                        </div>

                        {/* Bottom strip */}
                        <div className="border-t border-white/20 lg:border-gray-100 px-6 sm:px-8 lg:px-12 py-3 flex items-center justify-end gap-6">
                            {heroCount > 1 && (
                                <div className="flex items-center gap-1.5 shrink-0">
                                    {heroSlides.map((_, idx) => (
                                        <button
                                            key={idx}
                                            type="button"
                                            onClick={() => setHeroSlideIndex(idx)}
                                            aria-label={`Property ${idx + 1}`}
                                            className={`transition-all duration-300 ${idx === activeHeroIndex ? 'w-5 h-[2px] bg-white lg:bg-gray-900' : 'w-[2px] h-[2px] bg-white/35 lg:bg-gray-300 rounded-full hover:bg-white/60 lg:hover:bg-gray-500'}`}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </section>

                {/* ── Search & sort bar ── */}
                <div className="bg-white border-b border-gray-200">
                    <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-12 py-5 flex flex-col gap-4">
                        <div className="relative max-w-3xl w-full">
                            <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                            <input
                                type="text"
                                placeholder="Search by property name, city or country…"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-11 pr-4 py-3 border border-gray-200 bg-white text-gray-900 placeholder-gray-400 font-poppinsLight text-sm focus:outline-none focus:border-gray-900 transition-colors duration-200"
                            />
                        </div>
                        <div className="flex flex-wrap gap-3 items-center">
                            <div className="border border-gray-200 bg-white px-3 py-2">
                                <select
                                    value={sortBy}
                                    onChange={(e) => setSortBy(e.target.value)}
                                    className="bg-transparent border-0 text-gray-700 text-[11px] tracking-[0.15em] uppercase font-poppinsRegular focus:outline-none cursor-pointer pr-4"
                                >
                                    <option value="latest">Latest added</option>
                                    <option value="rating-high">Highest rated</option>
                                    <option value="name-az">Name: A – Z</option>
                                    <option value="name-za">Name: Z – A</option>
                                </select>
                            </div>
                            {hasActiveFilters && (
                                <button
                                    type="button"
                                    onClick={clearFilters}
                                    className="border border-gray-200 bg-white px-4 py-2 text-[11px] tracking-[0.15em] uppercase font-poppinsRegular text-gray-700 hover:border-gray-900 hover:text-gray-900 transition-colors duration-200"
                                >
                                    Clear filters
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={() => setIsSidebarOpen(true)}
                                className="lg:hidden inline-flex items-center gap-2 border border-gray-200 bg-white px-4 py-2 text-[11px] tracking-[0.15em] uppercase font-poppinsRegular text-gray-700"
                            >
                                <FaFilter size={11} />
                                Filters
                            </button>
                            <span className="text-[11px] tracking-[0.15em] uppercase text-gray-400 font-poppinsLight ml-auto">
                                {filteredProperties.length}{' '}
                                {filteredProperties.length === 1 ? 'property' : 'properties'}
                            </span>
                        </div>
                    </div>
                </div>

                {/* ── Body: sidebar + grid ── */}
                <div id="stays-grid" className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-12 py-8">
                    <div className="flex gap-8">

                        {/* Filters sidebar */}
                        <aside
                            className={`${isSidebarOpen ? 'fixed inset-0 z-50 bg-black/60 backdrop-blur-sm lg:relative lg:bg-transparent' : 'hidden lg:block'} lg:w-72 flex-shrink-0`}
                            onClick={(e) => { if (e.target === e.currentTarget) setIsSidebarOpen(false); }}
                        >
                            <div className={`${isSidebarOpen ? 'fixed right-0 top-0 bottom-0 w-80 bg-white overflow-y-auto border-l border-gray-200' : ''} lg:sticky lg:top-4 bg-white border border-gray-200`} style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.06)' }}>

                                <div className="px-6 pt-6 pb-4 border-b border-gray-100 flex justify-between items-center">
                                    <div>
                                        <p className="text-[10px] tracking-[0.3em] uppercase font-poppinsLight text-gray-400 mb-0.5">Refine</p>
                                        <h3 className="font-AXONALLPrimeRegular uppercase tracking-wide text-gray-900 text-lg">Filters</h3>
                                    </div>
                                    {isSidebarOpen && (
                                        <button onClick={() => setIsSidebarOpen(false)} className="lg:hidden p-2 text-gray-500 hover:text-gray-900 hover:bg-gray-100 transition-colors">
                                            <FaTimes size={16} />
                                        </button>
                                    )}
                                </div>

                                <div className="p-4 space-y-1">
                                    {/* Countries */}
                                    {countries.length > 0 && (
                                        <div className="border border-gray-100 overflow-hidden">
                                            <button
                                                onClick={() => toggleSection('countries')}
                                                className="w-full px-5 py-4 flex justify-between items-center hover:bg-gray-50 transition-colors"
                                            >
                                                <span className="text-[10px] tracking-[0.25em] uppercase font-poppinsRegular text-gray-700">Country</span>
                                                {expandedSections.countries
                                                    ? <FaChevronUp className="text-gray-400" size={11} />
                                                    : <FaChevronDown className="text-gray-400" size={11} />}
                                            </button>
                                            {expandedSections.countries && (
                                                <div className="px-5 pb-5 space-y-3 max-h-64 overflow-y-auto border-t border-gray-100">
                                                    <div className="pt-3" />
                                                    {countries.map((c) => (
                                                        <label key={c.countryCode} className="flex items-center cursor-pointer group">
                                                            <div className="relative">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={selectedCountries.includes(c.countryCode)}
                                                                    onChange={() =>
                                                                        setSelectedCountries((prev) =>
                                                                            prev.includes(c.countryCode)
                                                                                ? prev.filter((x) => x !== c.countryCode)
                                                                                : [...prev, c.countryCode]
                                                                        )
                                                                    }
                                                                    className="sr-only peer"
                                                                />
                                                                <div className="w-4 h-4 border border-gray-300 bg-white peer-checked:bg-gray-900 peer-checked:border-gray-900 transition-all flex items-center justify-center">
                                                                    {selectedCountries.includes(c.countryCode) && (
                                                                        <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                                                        </svg>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <span className={`ml-3 text-xs font-poppinsLight transition-colors ${selectedCountries.includes(c.countryCode) ? 'text-gray-900 font-poppinsRegular' : 'text-gray-500 group-hover:text-gray-900'}`}>
                                                                {c.countryName}
                                                                <span className="ml-1 text-gray-300">({c.count})</span>
                                                            </span>
                                                        </label>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Rating */}
                                    <div className="border border-gray-100 overflow-hidden">
                                        <button
                                            onClick={() => toggleSection('rating')}
                                            className="w-full px-5 py-4 flex justify-between items-center hover:bg-gray-50 transition-colors"
                                        >
                                            <span className="text-[10px] tracking-[0.25em] uppercase font-poppinsRegular text-gray-700">Star Rating</span>
                                            {expandedSections.rating
                                                ? <FaChevronUp className="text-gray-400" size={11} />
                                                : <FaChevronDown className="text-gray-400" size={11} />}
                                        </button>
                                        {expandedSections.rating && (
                                            <div className="px-5 pb-5 space-y-3 border-t border-gray-100">
                                                <div className="pt-3" />
                                                {ratingOptions.map((opt) => (
                                                    <label key={opt.value} className="flex items-center cursor-pointer group">
                                                        <div className="relative">
                                                            <input
                                                                type="radio"
                                                                name="rating"
                                                                checked={selectedRating === opt.value}
                                                                onChange={() => setSelectedRating(opt.value)}
                                                                className="sr-only peer"
                                                            />
                                                            <div className="w-4 h-4 border border-gray-300 rounded-full bg-white peer-checked:bg-gray-900 peer-checked:border-gray-900 transition-all flex items-center justify-center">
                                                                {selectedRating === opt.value && <div className="w-1.5 h-1.5 bg-white rounded-full" />}
                                                            </div>
                                                        </div>
                                                        <span className={`ml-3 text-xs font-poppinsLight transition-colors ${selectedRating === opt.value ? 'text-gray-900 font-poppinsRegular' : 'text-gray-500 group-hover:text-gray-900'}`}>
                                                            {opt.label}
                                                        </span>
                                                    </label>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    {hasActiveFilters && (
                                        <div className="pt-3 pb-1">
                                            <button
                                                onClick={clearFilters}
                                                className="w-full py-3.5 bg-gray-900 text-white text-[10px] tracking-[0.25em] uppercase font-poppinsRegular hover:bg-black transition-colors duration-200"
                                            >
                                                Reset Filters
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </aside>

                        {/* Main content */}
                        <main className="flex-1">
                            {error && (
                                <div className="bg-red-50 border-l-2 border-red-500 text-red-700 px-5 py-4 mb-6">
                                    <span className="text-sm font-poppinsMedium">⚠️ {error}</span>
                                </div>
                            )}

                            {loading && currentPage === 1 ? (
                                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                                    {[...Array(6)].map((_, i) => (
                                        <div key={i} className="bg-white overflow-hidden animate-pulse border border-gray-100">
                                            <div className="h-52 bg-gray-100" />
                                            <div className="p-4 space-y-2.5">
                                                <div className="h-4 bg-gray-100 w-3/4" />
                                                <div className="h-3 bg-gray-100 w-1/2" />
                                                <div className="h-3 bg-gray-100 w-2/3" />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : filteredProperties.length === 0 ? (
                                <div className="text-center py-20 bg-white border border-gray-200">
                                    <FaBuilding className="text-gray-200 mx-auto mb-5" size={40} />
                                    <p className="text-[10px] tracking-[0.3em] uppercase font-poppinsLight text-gray-400 mb-2">No Results</p>
                                    <h3 className="font-AXONALLPrimeRegular uppercase tracking-wide text-gray-900 text-xl mb-4">No Properties Found</h3>
                                    <p className="text-gray-400 font-poppinsLight text-sm mb-8 max-w-sm mx-auto">
                                        No off-market stays match your current filters. Try adjusting your search.
                                    </p>
                                    <button
                                        onClick={clearFilters}
                                        className="px-8 py-3 bg-gray-900 text-white text-[10px] tracking-[0.25em] uppercase font-poppinsRegular hover:bg-black transition-colors duration-200"
                                    >
                                        Clear All Filters
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                                        {filteredProperties.map((property) => {
                                            const cover = getCoverImage(property);
                                            const rating = formatRating(property.sabreRating);
                                            const city = property.location?.city;
                                            const country = property.location?.countryName;
                                            const locationLabel = [city, country].filter(Boolean).join(', ');
                                            const hasSpecialCommission = property.specialCommission?.enabled;
                                            const commissionBd = hasSpecialCommission
                                                ? getOffMarketSpecialCommissionBreakdown(property.specialCommission)
                                                : null;
                                            const hasPerks = property.axonallPerks?.enabled && Array.isArray(property.axonallPerks?.items) && property.axonallPerks.items.length > 0;
                                            const commissionLabel = hasSpecialCommission
                                                ? formatCommissionLabel(commissionBd, property.specialCommission)
                                                : null;

                                            return (
                                                <div
                                                    key={property._id}
                                                    className="relative h-[340px] sm:h-[320px] bg-black overflow-hidden group cursor-pointer"
                                                    style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.10)' }}
                                                    onClick={() => goToHotelProperty(property)}
                                                >
                                                    {/* Background image */}
                                                    <div className="absolute inset-0 z-0">
                                                        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/25 to-black/10 z-10" />
                                                        <img
                                                            src={cover}
                                                            alt={property.name}
                                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                                        />
                                                    </div>

                                                    {/* Top: off-market label + favorite */}
                                                    <div className="absolute top-3 left-3 right-3 z-20 flex justify-between items-start">
                                                        <span className="px-2.5 py-1 bg-black/30 backdrop-blur-sm text-[9px] tracking-[0.22em] uppercase font-poppinsRegular text-white/90 border border-white/15">
                                                            Off-Market
                                                        </span>
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); toggleFavorite(property._id); }}
                                                            className="p-2 bg-white/15 backdrop-blur-sm border border-white/20 text-white hover:bg-white hover:text-black transition-all shrink-0"
                                                        >
                                                            {isFavorite(property._id) ? (
                                                                <FaHeart className="text-red-400" size={13} />
                                                            ) : (
                                                                <FaRegHeart size={13} />
                                                            )}
                                                        </button>
                                                    </div>

                                                    {/* Bottom: content */}
                                                    <div className="absolute bottom-0 left-0 right-0 p-5 z-20">
                                                        <div className="flex items-center gap-2 mb-3">
                                                            {rating && (
                                                                <div className="flex items-center gap-1 bg-white/15 backdrop-blur-sm border border-white/15 px-2.5 py-1">
                                                                    <FaStar className="text-amber-400" size={10} />
                                                                    <span className="text-[10px] font-poppinsRegular text-white">{rating}</span>
                                                                </div>
                                                            )}
                                                            {locationLabel && (
                                                                <div className="flex items-center gap-1.5 bg-white/10 backdrop-blur-sm border border-white/10 px-2.5 py-1 min-w-0">
                                                                    <FaMapMarkerAlt className="text-white/50 shrink-0" size={9} />
                                                                    <span className="text-[10px] font-poppinsLight text-white/80 truncate max-w-[9rem]">{locationLabel}</span>
                                                                </div>
                                                            )}
                                                        </div>

                                                        <h3 className="font-AXONALLPrimeRegular uppercase tracking-wide text-white text-lg leading-snug mb-3 line-clamp-2">
                                                            {property.name}
                                                        </h3>

                                                        {(commissionLabel || hasPerks) && (
                                                            <div className="flex flex-wrap gap-2 mb-4">
                                                                {commissionLabel && (
                                                                    <span className="inline-flex items-center bg-amber-50 text-amber-900 border border-amber-200 px-2.5 py-1.5 text-[9px] tracking-[0.16em] uppercase font-poppinsRegular">
                                                                        {commissionLabel}
                                                                    </span>
                                                                )}
                                                                {hasPerks && (
                                                                    <span className="inline-flex items-center gap-1.5 bg-white text-gray-900 border border-gray-200 px-2.5 py-1.5 text-[9px] tracking-[0.16em] uppercase font-poppinsRegular">
                                                                        <Sparkles className="w-3 h-3 text-gray-700" strokeWidth={1.75} />
                                                                        AXONALL Perks
                                                                    </span>
                                                                )}
                                                            </div>
                                                        )}

                                                        <div className="flex items-end justify-between gap-2">
                                                            <div className="min-w-0">
                                                                {property.chainName && (
                                                                    <span className="text-[9px] tracking-[0.2em] uppercase font-poppinsLight text-white/40 block mb-0.5">{property.chainName}</span>
                                                                )}
                                                                <div className="flex items-center gap-1.5 text-white/40">
                                                                    <FaBuilding size={9} className="shrink-0" />
                                                                    <span className="font-mono text-[9px] truncate">{property.hotelCode}</span>
                                                                </div>
                                                            </div>
                                                            <button
                                                                className="shrink-0 px-3 py-2 bg-white text-black text-[9px] tracking-[0.2em] uppercase font-poppinsRegular opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300 hover:bg-gray-100"
                                                                onClick={(e) => { e.stopPropagation(); goToHotelProperty(property); }}
                                                                aria-label="View property"
                                                            >
                                                                View
                                                                <FiArrowUpRight className="inline ml-1" strokeWidth={2} />
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>

                                    {/* Infinite scroll sentinel */}
                                    {hasMore && (
                                        <div ref={observerTarget} className="flex justify-center py-12">
                                            {loading && (
                                                <div className="flex items-center gap-2">
                                                    <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" />
                                                    <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
                                                    <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
                                                    <span className="ml-2 text-[11px] tracking-[0.2em] uppercase font-poppinsLight text-gray-400">Loading</span>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </>
                            )}
                        </main>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default OffMarketStaysPage;
